import { Button } from "@/components/kit";
import SvgIcon from "@/components/common/SvgIcon";
import Filter from "@/components/kit/Filters/Filters";
import { useQueryParams } from "@/hooks/general/useQueryParams";
import { ChangeEvent, useEffect, useMemo } from "react";
import TextboxWithFilter from "@/components/common/TextboxWithFilter";
import { useNavigate } from "react-router-dom";
import { debounce } from "@/utilize/debounce";

// import useGerOrganizations from "@/hooks/query/organization/useGerOrganizations";
// import OrganizationList from "./OrganizationsList";
import useBreadcrumbs from "@/hooks/store/useBreadcrumbs";
import EmptyStateWithIcon from "@/components/common/EmptyStateWithIcon";
import MspAdminList from "./MspAdminList";
import useGetUsers from "@/hooks/query/organization/useGetUsers";
import { TUserParamsType } from "@/features/organizations/types";
import useStoreMe from "@/hooks/store/useStoreMe";
import NoResult from "@/features/organizations/components/addOrg/NoResult";
import { useGetRoles } from "@/hooks/query/acl/useGetRoles";

const sortOptions = [
  { label: "Name", value: "NAME" },
  { label: "Email", value: "EMAIL" },
];
const userStateOptions = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

const MspAdmins = () => {
  const { data: roles } = useGetRoles();
  const filterRoles = roles?.filter((item) =>
    ["SUPERADMIN", "BILLING", "OWNER"].includes(item.name),
  );
  const { params } = useQueryParams<TUserParamsType>({
    page: 1,
    limit: 25,
    roleIds: filterRoles?.map((item) => item.id) ?? [],
  });

  const { data, isFetching } = useGetUsers(params);
  const { setRoutes } = useBreadcrumbs();
  const navigate = useNavigate();
  const meId = useStoreMe().me?.id;

  const hasFilter = useMemo(() => {
    const { page, limit, orderBy, orderDirection, ...restParams } = params;

    return Object.values(restParams).some((value) => value !== undefined);
  }, [params, data]);

  const sortedData = useMemo(() => {
    return data?.data.items.sort((a, b) => {
      if (a.id === meId) return -1;
      if (b.id === meId) return 1;
      return 0;
    });
  }, [data, meId]);
  const renderContent = useMemo(() => {
    const deviceGroups = sortedData;
    const isEmpty = !deviceGroups || deviceGroups.length === 0;
    const renderWithFilter = (children: React.ReactNode) => (
      <WithFilters>{children}</WithFilters>
    );
    if (hasFilter && isEmpty && !isFetching) {
      return renderWithFilter(<NoResult />);
    }

    if (!hasFilter && isEmpty && !isFetching) {
      return (
        <EmptyStateWithIcon
          title="No admins yet"
          description="Add admins"
          button={
            <Button
              variant="gradient"
              className="!flex items-center !gap-2 w-[116px] h-[40px]"
              onClick={() => navigate("/admins/add")}
            >
              <SvgIcon className="w-5 h-5" iconName="plus" fill="fill-white" />
              <span>Add</span>
            </Button>
          }
        />
      );
    }

    return (
      <WithFilters>
        <MspAdminList
          data={sortedData || []}
          isFetching={isFetching}
          meta={data?.data.meta}
        />
      </WithFilters>
    );
  }, [hasFilter, data, isFetching]);
  useEffect(() => {
    setRoutes([
      {
        name: "Admin center",
        path: "/admins",
      },
      {
        name: "MSP admins",
        description:
          "View and manage all IT administrators with access to this organization. Review their roles, assigned permissions, and MFA status to maintain secure and controlled account access.",
        helpLink:
          "https://help.trio.so/en/articles/12530390-msp-admins-in-trio",
      },
    ]);
    return () => {
      setRoutes([]);
    };
  }, [setRoutes]);

  return <div className=" pt-[18px] h-full">{renderContent}</div>;
};

export default MspAdmins;

const WithFilters = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  const { setParams, params, deleteParam } = useQueryParams<TUserParamsType>({
    page: 1,
    limit: 25,
    orderDirection: "DESC",
    orderBy: "NAME",
  });
  const debounced = debounce((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setParams({ ...params, page: 1, search: e.target.value });
    } else {
      deleteParam("search");
    }
  });

  return (
    <div className="h-full flex flex-col">
      <div className="px-8  flex flex-col ">
        <div className="flex items-center gap-2">
          <TextboxWithFilter
            value={params.search}
            placeholder="Search in name"
            onChange={debounced}
          />

          <Button
            variant="gradient"
            className="!flex items-center !gap-2 w-[116px] h-[40px]"
            onClick={() => navigate("/admins/add")}
          >
            <SvgIcon className="w-5 h-5" iconName="plus" fill="fill-white" />
            <span>Add</span>
          </Button>
        </div>
        <div className="mt-2 mb-4">
          <Filter<typeof params>
            onFiltersChange={(newFilters) => {
              setParams({ ...params, ...newFilters, page: 1 });
            }}
            values={params}
            onClearFilter={() => {
              deleteParam("search");
              deleteParam("orderBy");
              deleteParam("userState");
              deleteParam("page");
              deleteParam("limit");
              deleteParam("orderDirection");
            }}
          >
            <Filter.FilterSort
              label="SortBy"
              keyName={"orderBy"}
              options={sortOptions}
              dataToShow={
                sortOptions.find((option) => option.value === params.orderBy)
                  ?.label
              }
            />
            <Filter.FilterSort
              label="User State"
              keyName={"userState"}
              options={userStateOptions}
              dataToShow={
                userStateOptions.find(
                  (option) => option.value === params.userState,
                )?.label
              }
            />
          </Filter>
        </div>
      </div>

      {children}
    </div>
  );
};
